import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  DevHubBox,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import { SystemContext } from '../../contexts/SystemContext';
import {
  ServiceOffering,
  ServiceOfferingSelect,
} from '../common/ServiceOfferingSelect';
import { EditAppMutation } from './queries/InsightsQueries';
import AppInfoItem from '../common/AppInfoItem';

interface Props {
  applicationId: string;
  serviceOfferings: ServiceOffering[];
  edit: boolean;
  hasEditPermission: boolean;
}

/**
 * Editable service offerings field for the Insights About page.
 */
export const EditableServiceOfferings = ({
  applicationId,
  serviceOfferings,
  edit,
  hasEditPermission,
}: Props) => {
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);
  const [editField, setEditField] = useState(false);
  const [selectedServiceOfferings, setSelectedServiceOfferings] = useState<
    ServiceOffering[]
  >(serviceOfferings ?? []);

  const [editApp, { loading }] = useMutation(EditAppMutation, {
    refetchQueries: [
      {
        query: GetApplicationQuery,
        variables: applicationVariables,
      },
      'GetApplicationDetails',
    ],
  });

  const handleEdit = () => {
    setSelectedServiceOfferings(serviceOfferings ?? []);
    setEditField(prev => !prev);
  };

  const handleClose = () => {
    setEditField(false);
    setSelectedServiceOfferings(serviceOfferings ?? []);
  };

  const handleSubmit = () => {
    editApp({
      variables: {
        input: {
          applicationId: Number(applicationId),
          iopServiceOfferings: selectedServiceOfferings.map(so => so.number),
        },
      },
    });
    setEditField(false);
  };

  const displayText =
    serviceOfferings && serviceOfferings.length > 0
      ? serviceOfferings.map(so => so.name).join(', ')
      : 'None';

  return (
    <DevHubBox display="flex">
      {edit && editField ? (
        <DevHubBox flex={1}>
          <ServiceOfferingSelect
            multiple
            selectedServiceOfferings={selectedServiceOfferings}
            setSelectedServiceOfferings={setSelectedServiceOfferings}
            helperText="Select one or more service offerings"
            required={false}
          />
        </DevHubBox>
      ) : (
        <AppInfoItem
          primaryText="IOP Service Offerings:"
          secondaryText={displayText}
        />
      )}
      {edit && !editField && (
        <>
          {loading ? (
            <DevHubCircularProgress thickness={6} size={22} />
          ) : (
            <DevHubTooltip title="Edit">
              <DevHubBox>
                <DevHubIconButton
                  size="medium"
                  disabled={!hasEditPermission}
                  onClick={handleEdit}
                >
                  <DevHubDynamicIcon iconName="Edit" />
                </DevHubIconButton>
              </DevHubBox>
            </DevHubTooltip>
          )}
        </>
      )}
      {edit && editField && (
        <DevHubBox display="inline-flex" ml={4} pt={2}>
          <DevHubTooltip title="Cancel">
            <DevHubBox>
              <DevHubIconButton onClick={handleClose}>
                <DevHubDynamicIcon iconName="CancelOutlined" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
          <DevHubTooltip title="Save">
            <DevHubBox>
              <DevHubIconButton onClick={handleSubmit}>
                <DevHubDynamicIcon iconName="Save" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
        </DevHubBox>
      )}
    </DevHubBox>
  );
};
